import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';

import NotificationBadge from '../../components/ui/NotificationBadge';
import MetricCard from './components/MetricCard';
import QuickActionCard from './components/QuickActionCard';
import ActivityFeedItem from './components/ActivityFeedItem';
import UpcomingSessionCard from './components/UpcomingSessionCard';
import WeeklyProgressChart from './components/WeeklyProgressChart';
import GoalProgressCard from './components/GoalProgressCard';
import Icon from '../../components/AppIcon';

const MemberDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isAuthenticated, loading: authLoading, unreadCount } = useAuth();
  const [activeTab, setActiveTab] = useState('feed');
  const [metricsData, setMetricsData] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user?.id && currentTenant?.id) {
      loadDashboardData();
    }
  }, [user, currentTenant]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load user's workouts
      const { data: workoutsData, error: workoutsError } = await supabase?.from('workouts')?.select('*')?.eq('user_id', user?.id)?.eq('tenant_id', currentTenant?.id)?.order('created_at', { ascending: false })?.limit(10);

      if (workoutsError) throw workoutsError;

      // Load upcoming classes
      const { data: bookingsData, error: bookingsError } = await supabase?.from('class_bookings')?.select('*, classes!inner(*, user_profiles(full_name))')?.eq('user_id', user?.id)?.gte('classes.scheduled_at', new Date()?.toISOString())?.order('classes(scheduled_at)', { ascending: true })?.limit(5);

      if (bookingsError) throw bookingsError;
      setUpcomingSessions(bookingsData || []);

      // Load nutrition logs for today
      const today = new Date();
      today?.setHours(0, 0, 0, 0);
      const { data: nutritionData, error: nutritionError } = await supabase?.from('nutrition_logs')?.select('*')?.eq('user_id', user?.id)?.eq('tenant_id', currentTenant?.id)?.gte('logged_at', today?.toISOString());

      if (nutritionError) throw nutritionError;

      // Calculate metrics
      const totalCalories = nutritionData?.reduce((sum, log) => sum + (log?.total_calories || 0), 0) || 0;
      const weeklyWorkouts = workoutsData?.filter((w) => {
        const workoutDate = new Date(w.created_at);
        const weekAgo = new Date();
        weekAgo?.setDate(weekAgo?.getDate() - 7);
        return workoutDate >= weekAgo;
      })?.length || 0;

      setMetricsData([
      {
        icon: 'Flame',
        iconColor: 'var(--color-accent)',
        title: "Today's Calories",
        value: totalCalories?.toString(),
        unit: 'kcal',
        subtitle: totalCalories > 0 ? 'Great progress today!' : 'Start tracking your meals',
        trend: 'up',
        trendValue: '+12%'
      },
      {
        icon: 'Activity',
        iconColor: 'var(--color-success)',
        title: 'Weekly Workouts',
        value: weeklyWorkouts?.toString(),
        unit: 'sessions',
        subtitle: `${Math.max(0, 7 - weeklyWorkouts)} more to reach goal`,
        trend: 'up',
        trendValue: `+${weeklyWorkouts}`
      },
      {
        icon: 'Target',
        iconColor: 'var(--color-primary)',
        title: 'Goal Progress',
        value: '68',
        unit: '%',
        subtitle: 'On track for this month',
        trend: 'up',
        trendValue: '+8%'
      },
      {
        icon: 'Award',
        iconColor: 'var(--color-warning)',
        title: 'Achievements',
        value: workoutsData?.length?.toString() || '0',
        unit: 'workouts',
        subtitle: 'Total completed',
        trend: 'up',
        trendValue: '+3'
      }]
      );

      // Set activity feed from workouts
      setActivityFeed(workoutsData?.slice(0, 5)?.map((workout) => ({
        type: 'workout',
        avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14f99bc91-1767522014091.png",
        avatarAlt: 'Member avatar',
        userName: user?.email?.split('@')?.[0] || 'You',
        action: 'completed a workout',
        details: `${workout?.title} - ${workout?.duration_minutes} minutes | ${workout?.calories_burned} calories burned`,
        timestamp: workout?.created_at,
        likes: 0,
        comments: 0
      })) || []);
    } catch (err) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
  {
    icon: 'Play',
    iconColor: 'var(--color-accent)',
    title: 'Start Workout',
    description: 'Begin your training session with personalized exercises',
    onClick: () => navigate('/workout-library')
  },
  {
    icon: 'Calendar',
    iconColor: 'var(--color-primary)',
    title: 'Book Class',
    description: 'Reserve your spot in upcoming group fitness classes',
    onClick: () => navigate('/class-schedule')
  },
  {
    icon: 'Clock',
    iconColor: 'var(--color-success)',
    title: 'View Schedule',
    description: 'Check your upcoming sessions and appointments',
    onClick: () => navigate('/class-schedule')
  },
  {
    icon: 'Apple',
    iconColor: 'var(--color-warning)',
    title: 'Track Nutrition',
    description: 'Log your meals and monitor daily calorie intake',
    onClick: () => navigate('/nutrition-tracker')
  }];


  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>);

  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-destructive/10 border border-destructive rounded-lg p-6 text-center">
            <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
            <p className="text-destructive text-lg font-semibold mb-2">Error Loading Dashboard</p>
            <p className="text-muted-foreground mb-4">{error}</p>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 md:py-8 pb-24 md:pb-8">
        {/* Welcome Section */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                Welcome Back! 👋
              </h1>
              <p className="text-muted-foreground">
                {currentTenant?.name || 'Your Fitness Journey'}
              </p>
            </div>
            {unreadCount > 0 && <NotificationBadge count={unreadCount} />}
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          {metricsData?.map((metric, index) =>
          <MetricCard key={index} {...metric} />
          )}
        </div>

        {/* Quick Actions */}
        <div className="mb-6 md:mb-8">
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions?.map((action, index) =>
            <QuickActionCard key={index} {...action} />
            )}
          </div>
        </div>

        {/* Main Content Tabs */}
        <div className="bg-card rounded-2xl shadow-lg p-4 md:p-6 mb-6">
          <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto">
            {[
            { id: 'feed', label: 'Activity Feed', icon: 'Activity' },
            { id: 'sessions', label: 'Upcoming Sessions', icon: 'Calendar' },
            { id: 'progress', label: 'Progress', icon: 'TrendingUp' }]?.
            map((tab) =>
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 rounded-t-lg transition-smooth whitespace-nowrap ${
              activeTab === tab?.id ?
              'bg-primary text-primary-foreground shadow-md' :
              'text-muted-foreground hover:text-foreground hover:bg-muted'}`
              }>

                <Icon name={tab?.icon} size={18} />
                <span className="font-medium">{tab?.label}</span>
              </button>
            )}
          </div>

          {/* Activity Feed Tab */}
          {activeTab === 'feed' &&
          <div className="space-y-4">
              {activityFeed?.length === 0 ?
            <div className="text-center py-12">
                  <Icon name="Activity" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground mb-4">No recent activity</p>
                  <p className="text-sm text-muted-foreground">Start a workout to see your activity here</p>
                </div> :

            activityFeed?.map((activity, index) =>
            <ActivityFeedItem key={index} {...activity} />
            )
            }
            </div>
          }

          {/* Upcoming Sessions Tab */}
          {activeTab === 'sessions' &&
          <div className="space-y-4">
              {upcomingSessions?.length === 0 ?
            <div className="text-center py-12">
                  <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground mb-4">No upcoming sessions</p>
                  <button
                onClick={() => navigate('/class-schedule')}
                className="text-primary hover:underline font-medium">

                    Browse available classes
                  </button>
                </div> :

            upcomingSessions?.map((session) =>
            <UpcomingSessionCard
              key={session?.id}
              title={session?.classes?.title}
              instructor={session?.classes?.user_profiles?.full_name || 'Instructor'}
              instructorImage="https://img.rocket.new/generatedImages/rocket_gen_img_15f002d89-1763294705593.png"
              instructorImageAlt="Instructor avatar"
              date={new Date(session?.classes?.scheduled_at)?.toLocaleDateString()}
              time={new Date(session?.classes?.scheduled_at)?.toLocaleTimeString()}
              duration={session?.classes?.duration_minutes}
              type={session?.classes?.category}
              location="Main Studio"
              spotsLeft={session?.classes?.max_capacity}
              onManage={() => {}}
              onCancel={() => {}} />

            )
            }
            </div>
          }

          {/* Progress Tab */}
          {activeTab === 'progress' &&
          <div className="space-y-6">
              <WeeklyProgressChart data={[]} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GoalProgressCard
                title="Weight Loss Goal"
                current={68}
                target={75}
                unit="kg"
                progress={75}
                percentage={75}
                icon="TrendingDown"
                color="var(--color-accent)"
                iconColor="var(--color-accent)"
                daysLeft={30} />

                <GoalProgressCard
                title="Workout Streak"
                current={12}
                target={30}
                unit="days"
                progress={40}
                percentage={40}
                icon="Flame"
                color="var(--color-warning)"
                iconColor="var(--color-warning)"
                daysLeft={18} />

              </div>
            </div>
          }
        </div>
      </main>
      <MobileBottomNav />
    </div>);

};

export default MemberDashboard;